import { useCallback, useEffect, useState } from "react";
import { Building2, CreditCard, Edit3, KeyRound, ShieldCheck, UserPlus, Users, X } from "lucide-react";
import { api } from "../../services/api";
import { useAuth } from "../../auth/AuthContext";
import { StatusPill, WorkflowToolbar } from "./AdminPanelParts";
import { useAdminResource } from "./useAdminResource";
import { ToastStack, useToast } from "./useToast";

const privilegeOptions = [
  ["dashboard", "Dashboard"],
  ["products", "Products"],
  ["orders", "Orders"],
  ["returns", "Returns"],
  ["channels", "Channels"],
  ["warehouse", "Warehouse"],
  ["reports", "Reports"],
  ["cms", "Website CMS"],
  ["media", "Media Library"],
  ["customers", "Customers"],
  ["reviews", "Reviews"],
  ["notifications", "Notifications"],
  ["newsletter", "Newsletter"],
  ["activity", "Activity Logs"],
  ["support", "Support"],
  ["automation", "Automation"],
  ["settings", "Settings"],
];

const SUPER_ADMIN_EMAIL = "admin@bobbinusa.com";

const rolePresets = {
  owner: privilegeOptions.reduce((acc, [key]) => ({ ...acc, [key]: true }), {}),
  manager: {
    dashboard: true,
    products: true,
    orders: true,
    returns: true,
    warehouse: true,
    reports: true,
    customers: true,
    reviews: true,
    media: true,
    notifications: true,
    activity: true,
    support: true,
  },
  staff: {
    dashboard: true,
    products: true,
    media: true,
    orders: true,
    returns: true,
    warehouse: true,
    customers: true,
    support: true,
  },
  viewer: {
    dashboard: true,
    reports: true,
    activity: true,
  },
};

const fallbackSettingsPayload = {
  settings: {
    business: {
      name: "Bobbin USA",
      supportEmail: "support@bobbinusa.example",
      phone: "800-555-0184",
      taxId: "",
      address: {
        line1: "125 Industrial Way",
        city: "Paterson",
        state: "NJ",
        postalCode: "07501",
        country: "United States",
      },
    },
    payment: {
      defaultProvider: "manual",
      captureMode: "automatic",
      requirePaidBeforeFulfillment: true,
      paypal: {
        enabled: false,
        mode: "sandbox",
        clientId: "",
        clientSecret: "",
        clientSecretConfigured: false,
        currency: "USD",
      },
    },
  },
  users: [],
};

const emptyAdminForm = {
  name: "",
  email: "",
  password: "",
  adminRole: "staff",
  privileges: rolePresets.staff,
};

const tabs = [
  ["Business", "business", Building2],
  ["Payments", "payment", CreditCard],
  ["Admins", "admins", ShieldCheck],
  ["Users", "users", Users],
];

function flattenBusiness(settings) {
  return {
    name: settings.business?.name || "",
    supportEmail: settings.business?.supportEmail || "",
    phone: settings.business?.phone || "",
    taxId: settings.business?.taxId || "",
    line1: settings.business?.address?.line1 || "",
    city: settings.business?.address?.city || "",
    state: settings.business?.address?.state || "",
    postalCode: settings.business?.address?.postalCode || "",
    country: settings.business?.address?.country || "United States",
  };
}

function normalizePaymentSettings(payment = {}) {
  const fallback = fallbackSettingsPayload.settings.payment;

  return {
    ...fallback,
    ...payment,
    paypal: {
      ...fallback.paypal,
      ...(payment.paypal || {}),
      clientSecret: "",
    },
  };
}

function normalizePrivileges(adminRole = "staff", privileges = {}) {
  const preset = rolePresets[adminRole] || rolePresets.staff;

  return privilegeOptions.reduce((acc, [key]) => {
    acc[key] = privileges[key] === undefined ? Boolean(preset[key]) : Boolean(privileges[key]);
    return acc;
  }, {});
}

function formatDate(date) {
  if (!date) return "Not recorded";
  return new Intl.DateTimeFormat("en-US", {
    dateStyle: "medium",
    timeZone: "America/New_York",
  }).format(new Date(date));
}

export default function Setting() {
  const [activeTab, setActiveTab] = useState("business");
  const [businessForm, setBusinessForm] = useState(flattenBusiness(fallbackSettingsPayload.settings));
  const [paymentForm, setPaymentForm] = useState(normalizePaymentSettings(fallbackSettingsPayload.settings.payment));
  const [adminForm, setAdminForm] = useState(emptyAdminForm);
  const [adminModalOpen, setAdminModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const { dismiss, toast, toasts } = useToast();
  const { user: currentUser } = useAuth();

  const loadSettings = useCallback(async () => {
    return api.get("/settings");
  }, []);

  const { data, error, loading, reload, setData } = useAdminResource(loadSettings, fallbackSettingsPayload);
  const settings = data.settings || fallbackSettingsPayload.settings;
  const users = data.users || fallbackSettingsPayload.users;
  const isCurrentSuperAdmin = currentUser?.email === SUPER_ADMIN_EMAIL;
  const adminUsers = users.filter((user) => user.role === "admin");
  const normalUsers = users.filter((user) => user.role !== "admin");

  useEffect(() => {
    setBusinessForm(flattenBusiness(settings));
    setPaymentForm(normalizePaymentSettings(settings.payment));
  }, [settings]);

  useEffect(() => {
    if (error && !loading) toast(`Using preview settings: ${error}`, "warning");
  }, [error, loading, toast]);

  const updateForm = (setter) => (event) => {
    const { checked, name, type, value } = event.target;
    setter((current) => ({ ...current, [name]: type === "checkbox" ? checked : value }));
  };

  const updatePayPalForm = (event) => {
    const { checked, name, type, value } = event.target;
    setPaymentForm((current) => ({
      ...current,
      paypal: {
        ...(current.paypal || {}),
        [name]: type === "checkbox" ? checked : value,
      },
    }));
  };

  const updateAdminField = (event) => {
    const { name, value } = event.target;
    setAdminForm((current) => {
      if (name === "adminRole") {
        return { ...current, adminRole: value, privileges: normalizePrivileges(value, rolePresets[value]) };
      }
      return { ...current, [name]: value };
    });
  };

  const updateAdminPrivilege = (key) => {
    setAdminForm((current) => ({
      ...current,
      privileges: {
        ...normalizePrivileges(current.adminRole, current.privileges),
        [key]: !Boolean(current.privileges?.[key]),
      },
    }));
  };

  const openCreateAdmin = () => {
    setAdminForm(emptyAdminForm);
    setAdminModalOpen(true);
  };

  const closeCreateAdmin = () => {
    setAdminModalOpen(false);
    setAdminForm(emptyAdminForm);
  };

  const openEditAdmin = (user) => {
    const adminRole = user.adminRole || "staff";
    setEditingUser(user);
    setEditForm({
      name: user.name || "",
      email: user.email || "",
      phone: user.phone || "",
      password: "",
      role: user.role || "user",
      adminRole,
      isActive: Boolean(user.isActive),
      privileges: normalizePrivileges(adminRole, user.privileges || {}),
    });
  };

  const closeEditAdmin = () => {
    setEditingUser(null);
    setEditForm(null);
  };

  const updateEditField = (event) => {
    const { checked, name, type, value } = event.target;
    setEditForm((current) => {
      if (name === "adminRole") {
        return { ...current, adminRole: value, privileges: normalizePrivileges(value, rolePresets[value]) };
      }
      return { ...current, [name]: type === "checkbox" ? checked : value };
    });
  };

  const updateEditPrivilege = (key) => {
    setEditForm((current) => ({
      ...current,
      privileges: {
        ...normalizePrivileges(current.adminRole, current.privileges),
        [key]: !Boolean(current.privileges?.[key]),
      },
    }));
  };

  const replaceSettings = (nextSettings) => {
    setData((current) => ({ ...current, settings: nextSettings }));
  };

  const replaceUsers = (nextUsers) => {
    setData((current) => ({ ...current, users: nextUsers }));
  };

  const saveSection = async (section, payload) => {
    try {
      const data = await api.patch(`/settings/${section}`, payload);
      replaceSettings(data.settings);
      toast(`${section} settings saved`, "success");
    } catch (err) {
      toast(err.message, "error");
    }
  };

  const saveBusiness = (event) => {
    event.preventDefault();
    saveSection("business", {
      name: businessForm.name,
      supportEmail: businessForm.supportEmail,
      phone: businessForm.phone,
      taxId: businessForm.taxId,
      address: {
        line1: businessForm.line1,
        city: businessForm.city,
        state: businessForm.state,
        postalCode: businessForm.postalCode,
        country: businessForm.country,
      },
    });
  };

  const savePayment = (event) => {
    event.preventDefault();
    const paypal = paymentForm.paypal || {};

    saveSection("payment", {
      defaultProvider: paymentForm.defaultProvider,
      captureMode: paymentForm.captureMode,
      requirePaidBeforeFulfillment: Boolean(paymentForm.requirePaidBeforeFulfillment),
      paypal: {
        enabled: Boolean(paypal.enabled),
        mode: paypal.mode === "live" ? "live" : "sandbox",
        clientId: paypal.clientId || "",
        clientSecret: paypal.clientSecret || "",
        currency: String(paypal.currency || "USD").toUpperCase(),
      },
    });
  };

  const createAdmin = async (event) => {
    event.preventDefault();
    try {
      const data = await api.post("/auth/admin", {
        ...adminForm,
        privileges: normalizePrivileges(adminForm.adminRole, adminForm.privileges),
      });
      replaceUsers([data.user, ...users]);
      closeCreateAdmin();
      toast("Admin user created", "success");
    } catch (err) {
      toast(err.message, "error");
    }
  };

  const updateUserAccess = async (user, patch) => {
    try {
      const adminRole = patch.adminRole || user.adminRole || "staff";
      const nextRole = patch.role || user.role || "admin";
      const privileges =
        patch.privileges ||
        (patch.adminRole ? normalizePrivileges(adminRole, rolePresets[adminRole]) : normalizePrivileges(adminRole, user.privileges));

      const data = await api.patch(`/settings/users/${user._id || user.id}`, {
        name: patch.name === undefined ? user.name : patch.name,
        email: patch.email === undefined ? user.email : patch.email,
        phone: patch.phone === undefined ? user.phone : patch.phone,
        password: patch.password || "",
        role: nextRole,
        adminRole,
        privileges,
        isActive: patch.isActive === undefined ? user.isActive : patch.isActive,
      });
      replaceUsers(data.users || []);
      toast("User access updated", "success");
      return true;
    } catch (err) {
      toast(err.message, "error");
      return false;
    }
  };

  const saveEditedAdmin = async (event) => {
    event.preventDefault();
    if (!editingUser || !editForm) return;

    const saved = await updateUserAccess(editingUser, {
      role: editForm.role,
      name: editForm.name,
      email: editForm.email,
      phone: editForm.phone,
      password: editForm.password,
      adminRole: editForm.adminRole,
      privileges: normalizePrivileges(editForm.adminRole, editForm.privileges),
      isActive: editForm.isActive,
    });
    if (saved) closeEditAdmin();
  };

  const renderUserCard = (user) => {
    const userRole = user.adminRole || "staff";
    const privileges = normalizePrivileges(userRole, user.privileges || {});
    const isSuperAdmin = user.email === SUPER_ADMIN_EMAIL;
    const activePrivilegeCount = privilegeOptions.filter(([key]) => privileges[key]).length;
    const canEditUser = !isSuperAdmin || isCurrentSuperAdmin;

    return (
      <article className="rounded-2xl border border-gray-100 p-4" key={user._id || user.id}>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-base font-black text-gray-900">{user.name}</h3>
              {isSuperAdmin && <StatusPill tone="good">Super Admin</StatusPill>}
              <StatusPill tone={user.isActive ? "good" : "bad"}>{user.isActive ? "active" : "disabled"}</StatusPill>
              <StatusPill tone={user.role === "admin" ? "info" : "neutral"}>{user.role}</StatusPill>
            </div>
            <p className="mt-1 text-sm font-semibold text-gray-500">{user.email}</p>
            {user.phone && <p className="mt-1 text-xs font-bold text-gray-400">{user.phone}</p>}
            <p className="mt-1 text-xs font-bold text-gray-400">Created {formatDate(user.createdAt)}</p>
          </div>

          <div className="grid gap-3 sm:grid-cols-3 lg:w-[520px]">
            <div className="rounded-xl bg-gray-50 px-4 py-3">
              <p className="text-[10px] font-black uppercase text-gray-400">Admin level</p>
              <p className="mt-1 text-sm font-black capitalize text-gray-800">{user.role === "admin" ? userRole : "Customer"}</p>
            </div>
            <div className="rounded-xl bg-gray-50 px-4 py-3">
              <p className="text-[10px] font-black uppercase text-gray-400">Privileges</p>
              <p className="mt-1 text-sm font-black text-gray-800">
                {user.role === "admin" ? `${activePrivilegeCount}/${privilegeOptions.length}` : "Customer account"}
              </p>
            </div>
            <button
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 px-4 py-3 text-sm font-black text-gray-700 hover:border-[#4391d1] hover:text-[#4391d1] disabled:cursor-not-allowed disabled:opacity-50"
              disabled={!canEditUser}
              onClick={() => openEditAdmin(user)}
              type="button"
            >
              <Edit3 size={15} />
              Edit
            </button>
          </div>
        </div>
      </article>
    );
  };

  return (
    <div className="space-y-6">
      <ToastStack dismiss={dismiss} toasts={toasts} />

      {adminModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-gray-950/55 p-4 backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="create-admin-title">
          <form className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-3xl bg-white shadow-2xl" onSubmit={createAdmin}>
            <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-gray-100 bg-white px-6 py-5">
              <div className="flex items-start gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-50 text-[#4391d1]">
                  <UserPlus size={20} />
                </span>
                <div>
                  <p className="text-xs font-black uppercase text-[#4391d1]">Admin Access</p>
                  <h2 className="mt-1 text-xl font-black text-gray-900" id="create-admin-title">Create Admin</h2>
                  <p className="mt-1 text-sm font-semibold text-gray-500">Create a secure admin account with exact dashboard privileges.</p>
                </div>
              </div>
              <button className="flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 text-gray-500 hover:border-[#4391d1] hover:text-[#4391d1]" onClick={closeCreateAdmin} type="button">
                <X size={18} />
              </button>
            </div>

            <div className="grid gap-5 px-6 py-5">
              <div className="grid gap-4 md:grid-cols-2">
                <label>
                  <span className="mb-2 block text-xs font-black uppercase text-gray-500">Name</span>
                  <input className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm font-semibold outline-none focus:border-[#4391d1]" name="name" onChange={updateAdminField} required value={adminForm.name} />
                </label>
                <label>
                  <span className="mb-2 block text-xs font-black uppercase text-gray-500">Email</span>
                  <input className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm font-semibold outline-none focus:border-[#4391d1]" name="email" onChange={updateAdminField} required type="email" value={adminForm.email} />
                </label>
                <label>
                  <span className="mb-2 block text-xs font-black uppercase text-gray-500">Temporary password</span>
                  <input className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm font-semibold outline-none focus:border-[#4391d1]" minLength={8} name="password" onChange={updateAdminField} required type="password" value={adminForm.password} />
                </label>
                <label>
                  <span className="mb-2 block text-xs font-black uppercase text-gray-500">Role</span>
                  <select className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm font-semibold outline-none focus:border-[#4391d1]" name="adminRole" onChange={updateAdminField} value={adminForm.adminRole}>
                    <option value="owner">Owner</option>
                    <option value="manager">Manager</option>
                    <option value="staff">Staff</option>
                    <option value="viewer">Viewer</option>
                  </select>
                </label>
              </div>

              <section className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
                <p className="mb-3 text-xs font-black uppercase text-gray-500">Privileges</p>
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                  {privilegeOptions.map(([key, label]) => (
                    <label className="flex items-center gap-2 rounded-xl bg-white px-3 py-2 text-xs font-black text-gray-700" key={key}>
                      <input checked={Boolean(adminForm.privileges?.[key])} onChange={() => updateAdminPrivilege(key)} type="checkbox" />
                      {label}
                    </label>
                  ))}
                </div>
              </section>
            </div>

            <div className="sticky bottom-0 flex flex-col-reverse gap-2 border-t border-gray-100 bg-white px-6 py-4 sm:flex-row sm:justify-end">
              <button className="rounded-xl border border-gray-200 px-5 py-3 text-sm font-black text-gray-700 hover:border-[#4391d1] hover:text-[#4391d1]" onClick={closeCreateAdmin} type="button">
                Cancel
              </button>
              <button className="rounded-xl bg-[#4391d1] px-5 py-3 text-sm font-black text-white hover:bg-blue-600" type="submit">
                Create Admin User
              </button>
            </div>
          </form>
        </div>
      )}

      {editingUser && editForm && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-gray-950/55 p-4 backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="edit-admin-title">
          <form className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-3xl bg-white shadow-2xl" onSubmit={saveEditedAdmin}>
            <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-gray-100 bg-white px-6 py-5">
              <div className="flex items-start gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-50 text-[#4391d1]">
                  <ShieldCheck size={20} />
                </span>
                <div>
                  <p className="text-xs font-black uppercase text-[#4391d1]">Account Access</p>
                  <h2 className="mt-1 text-xl font-black text-gray-900" id="edit-admin-title">
                    {editingUser.role === "admin" ? "Edit Admin" : "Edit User"}
                  </h2>
                  <p className="mt-1 text-sm font-semibold text-gray-500">{editingUser.name} - {editingUser.email}</p>
                </div>
              </div>
              <button className="flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 text-gray-500 hover:border-[#4391d1] hover:text-[#4391d1]" onClick={closeEditAdmin} type="button">
                <X size={18} />
              </button>
            </div>

            <div className="grid gap-5 px-6 py-5">
              {isCurrentSuperAdmin && (
                <section className="rounded-2xl border border-blue-100 bg-blue-50/40 p-4">
                  <div className="mb-4 flex items-center gap-2">
                    <KeyRound size={16} className="text-[#4391d1]" />
                    <p className="text-sm font-black text-gray-900">User Details</p>
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <label>
                      <span className="mb-2 block text-xs font-black uppercase text-gray-500">Name</span>
                      <input
                        className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-semibold outline-none focus:border-[#4391d1]"
                        name="name"
                        onChange={updateEditField}
                        required
                        value={editForm.name}
                      />
                    </label>
                    <label>
                      <span className="mb-2 block text-xs font-black uppercase text-gray-500">Email</span>
                      <input
                        className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-semibold outline-none focus:border-[#4391d1] disabled:bg-gray-100"
                        disabled={editingUser.email === SUPER_ADMIN_EMAIL}
                        name="email"
                        onChange={updateEditField}
                        required
                        type="email"
                        value={editForm.email}
                      />
                    </label>
                    <label>
                      <span className="mb-2 block text-xs font-black uppercase text-gray-500">Phone</span>
                      <input
                        className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-semibold outline-none focus:border-[#4391d1]"
                        name="phone"
                        onChange={updateEditField}
                        placeholder="Customer phone"
                        value={editForm.phone}
                      />
                    </label>
                    <label>
                      <span className="mb-2 block text-xs font-black uppercase text-gray-500">New Password</span>
                      <input
                        className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-semibold outline-none focus:border-[#4391d1]"
                        minLength={8}
                        name="password"
                        onChange={updateEditField}
                        placeholder="Leave blank to keep current password"
                        type="password"
                        value={editForm.password}
                      />
                    </label>
                  </div>
                </section>
              )}

              <div className="grid gap-4 md:grid-cols-3">
                <label>
                  <span className="mb-2 block text-xs font-black uppercase text-gray-500">Account role</span>
                  <select
                    className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm font-semibold outline-none focus:border-[#4391d1] disabled:bg-gray-100"
                    disabled={editingUser.email === SUPER_ADMIN_EMAIL}
                    name="role"
                    onChange={updateEditField}
                    value={editForm.role}
                  >
                    <option value="admin">Admin</option>
                    <option value="user">Customer</option>
                  </select>
                </label>
                <label>
                  <span className="mb-2 block text-xs font-black uppercase text-gray-500">Admin level</span>
                  <select
                    className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm font-semibold outline-none focus:border-[#4391d1] disabled:bg-gray-100"
                    disabled={editingUser.email === SUPER_ADMIN_EMAIL || editForm.role !== "admin"}
                    name="adminRole"
                    onChange={updateEditField}
                    value={editForm.adminRole}
                  >
                    <option value="owner">Owner</option>
                    <option value="manager">Manager</option>
                    <option value="staff">Staff</option>
                    <option value="viewer">Viewer</option>
                  </select>
                </label>
                <label className="flex items-center gap-3 rounded-xl border border-gray-100 px-4 py-3 text-sm font-black text-gray-700">
                  <input checked={Boolean(editForm.isActive)} disabled={editingUser.email === SUPER_ADMIN_EMAIL} name="isActive" onChange={updateEditField} type="checkbox" />
                  Account active
                </label>
              </div>

              {editForm.role === "admin" && (
                <section className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
                  <p className="mb-3 text-xs font-black uppercase text-gray-500">Privileges</p>
                  <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                    {privilegeOptions.map(([key, label]) => (
                      <label className="flex items-center gap-2 rounded-xl bg-white px-3 py-2 text-xs font-black text-gray-700" key={key}>
                        <input checked={Boolean(editForm.privileges?.[key])} disabled={editingUser.email === SUPER_ADMIN_EMAIL} onChange={() => updateEditPrivilege(key)} type="checkbox" />
                        {label}
                      </label>
                    ))}
                  </div>
                </section>
              )}
            </div>

            <div className="sticky bottom-0 flex flex-col-reverse gap-2 border-t border-gray-100 bg-white px-6 py-4 sm:flex-row sm:justify-end">
              <button className="rounded-xl border border-gray-200 px-5 py-3 text-sm font-black text-gray-700 hover:border-[#4391d1] hover:text-[#4391d1]" onClick={closeEditAdmin} type="button">
                Cancel
              </button>
              <button className="rounded-xl bg-[#4391d1] px-5 py-3 text-sm font-black text-white hover:bg-blue-600" type="submit">
                Save Changes
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="flex flex-col gap-4 rounded-2xl bg-white p-6 shadow-sm lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-sm font-black uppercase tracking-wide text-[#4391d1]">Settings</p>
          <h1 className="mt-2 text-3xl font-black text-gray-900">Business, payment, and admin access</h1>
          <p className="mt-2 max-w-3xl text-sm font-medium text-gray-500">
            Manage company profile details, PayPal checkout configuration, admin users, roles, and dashboard privileges.
          </p>
        </div>
        <WorkflowToolbar actions={[{ label: "Refresh", onClick: reload }]} />
      </div>

      <div className="flex flex-wrap gap-2 rounded-2xl bg-white p-2 shadow-sm">
        {tabs.map(([label, value, Icon]) => (
          <button
            className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-black ${activeTab === value ? "bg-[#4391d1] text-white" : "text-gray-600 hover:bg-gray-50"}`}
            key={value}
            onClick={() => setActiveTab(value)}
            type="button"
          >
            <Icon size={16} />
            {label}
          </button>
        ))}
      </div>

      {activeTab === "business" && (
        <form className="rounded-2xl bg-white p-6 shadow-sm" onSubmit={saveBusiness}>
          <h2 className="text-xl font-black text-gray-900">Business Info</h2>
          <div className="mt-5 grid gap-3 md:grid-cols-2">
            {[
              ["name", "Business name"],
              ["supportEmail", "Support email"],
              ["phone", "Phone"],
              ["taxId", "Tax ID"],
              ["line1", "Address"],
              ["city", "City"],
              ["state", "State"],
              ["postalCode", "Postal code"],
              ["country", "Country"],
            ].map(([name, placeholder]) => (
              <input
                className="rounded-xl border border-gray-200 px-4 py-3 text-sm font-semibold outline-none focus:border-[#4391d1]"
                key={name}
                name={name}
                onChange={updateForm(setBusinessForm)}
                placeholder={placeholder}
                value={businessForm[name] || ""}
              />
            ))}
          </div>
          <button className="mt-5 rounded-xl bg-[#4391d1] px-5 py-3 text-sm font-black text-white hover:bg-blue-600" type="submit">
            Save Business Info
          </button>
        </form>
      )}

      {activeTab === "payment" && (
        <form className="space-y-5 rounded-2xl bg-white p-6 shadow-sm" onSubmit={savePayment}>
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div>
              <h2 className="text-xl font-black text-gray-900">Payment Methods</h2>
              <p className="mt-1 text-sm font-semibold text-gray-500">
                Manage checkout behavior and PayPal credentials used by the storefront.
              </p>
            </div>
            <StatusPill tone={paymentForm.paypal?.enabled ? "good" : "neutral"}>
              PayPal {paymentForm.paypal?.enabled ? "enabled" : "disabled"}
            </StatusPill>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <label>
              <span className="mb-2 block text-xs font-black uppercase text-gray-500">Default provider</span>
              <select
                className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm font-semibold"
                name="defaultProvider"
                onChange={updateForm(setPaymentForm)}
                value={paymentForm.defaultProvider || "manual"}
              >
                <option value="manual">Manual</option>
                <option value="paypal">PayPal</option>
              </select>
            </label>
            <label>
              <span className="mb-2 block text-xs font-black uppercase text-gray-500">Capture mode</span>
              <select
                className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm font-semibold"
                name="captureMode"
                onChange={updateForm(setPaymentForm)}
                value={paymentForm.captureMode || "automatic"}
              >
                <option value="automatic">Automatic</option>
                <option value="manual">Manual</option>
              </select>
            </label>
            <label className="flex items-center gap-3 rounded-xl border border-gray-100 p-4 text-sm font-black text-gray-700">
              <input
                checked={Boolean(paymentForm.requirePaidBeforeFulfillment)}
                name="requirePaidBeforeFulfillment"
                onChange={updateForm(setPaymentForm)}
                type="checkbox"
              />
              Require paid order before fulfillment
            </label>
          </div>

          <section className="rounded-2xl border border-blue-100 bg-blue-50/40 p-5">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-xs font-black uppercase tracking-wide text-[#4391d1]">PayPal Checkout</p>
                <h3 className="mt-1 text-lg font-black text-gray-900">API credentials</h3>
                <p className="mt-1 text-xs font-semibold text-gray-500">
                  Client secret is stored on the server and is never returned to the browser.
                </p>
              </div>
              <label className="flex items-center gap-3 rounded-xl bg-white px-4 py-3 text-sm font-black text-gray-700 shadow-sm">
                <input checked={Boolean(paymentForm.paypal?.enabled)} name="enabled" onChange={updatePayPalForm} type="checkbox" />
                Enable PayPal
              </label>
            </div>

            <div className="mt-5 grid gap-4 md:grid-cols-2">
              <label>
                <span className="mb-2 block text-xs font-black uppercase text-gray-500">Mode</span>
                <select
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-semibold"
                  name="mode"
                  onChange={updatePayPalForm}
                  value={paymentForm.paypal?.mode || "sandbox"}
                >
                  <option value="sandbox">Sandbox</option>
                  <option value="live">Live</option>
                </select>
              </label>
              <label>
                <span className="mb-2 block text-xs font-black uppercase text-gray-500">Currency</span>
                <input
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-semibold uppercase"
                  maxLength={3}
                  name="currency"
                  onChange={updatePayPalForm}
                  placeholder="USD"
                  value={paymentForm.paypal?.currency || "USD"}
                />
              </label>
              <label className="md:col-span-2">
                <span className="mb-2 block text-xs font-black uppercase text-gray-500">Client ID</span>
                <input
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-semibold"
                  name="clientId"
                  onChange={updatePayPalForm}
                  placeholder="PayPal REST app client ID"
                  value={paymentForm.paypal?.clientId || ""}
                />
              </label>
              <label className="md:col-span-2">
                <span className="mb-2 flex items-center justify-between gap-3 text-xs font-black uppercase text-gray-500">
                  Client Secret
                  {paymentForm.paypal?.clientSecretConfigured && (
                    <span className="rounded-full bg-emerald-50 px-2 py-1 text-[10px] text-emerald-700">Saved</span>
                  )}
                </span>
                <input
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-semibold"
                  name="clientSecret"
                  onChange={updatePayPalForm}
                  placeholder={paymentForm.paypal?.clientSecretConfigured ? "Leave blank to keep existing secret" : "PayPal REST app client secret"}
                  type="password"
                  value={paymentForm.paypal?.clientSecret || ""}
                />
              </label>
            </div>
          </section>

          <button className="rounded-xl bg-[#4391d1] px-5 py-3 text-sm font-black text-white hover:bg-blue-600" type="submit">
            Save Payment Settings
          </button>
        </form>
      )}

      {activeTab === "admins" && (
        <section className="rounded-2xl bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="flex items-start gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-50 text-[#4391d1]">
                  <ShieldCheck size={20} />
                </span>
                <div>
                  <h2 className="text-xl font-black text-gray-900">Admin Roles & Privileges</h2>
                  <p className="mt-1 text-sm font-semibold text-gray-500">Manage active access and dashboard permissions for every admin account.</p>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <StatusPill tone="info">{adminUsers.length} admins</StatusPill>
              <button
                className="inline-flex items-center gap-2 rounded-xl bg-[#4391d1] px-4 py-3 text-sm font-black text-white hover:bg-blue-600"
                onClick={openCreateAdmin}
                type="button"
              >
                <UserPlus size={16} />
                Create Admin
              </button>
            </div>
          </div>

          <div className="mt-6 space-y-3">
            {adminUsers.length ? (
              adminUsers.map(renderUserCard)
            ) : (
              <div className="rounded-2xl border border-dashed border-gray-200 px-6 py-10 text-center">
                <KeyRound className="mx-auto text-gray-300" size={34} />
                <h3 className="mt-3 text-lg font-black text-gray-900">No admin accounts found</h3>
                <p className="mt-1 text-sm font-semibold text-gray-500">Create the first admin user from the popup.</p>
              </div>
            )}
          </div>
        </section>
      )}

      {activeTab === "users" && (
        <section className="rounded-2xl bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex items-start gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-50 text-[#4391d1]">
                <Users size={20} />
              </span>
              <div>
                <h2 className="text-xl font-black text-gray-900">Normal Users</h2>
                <p className="mt-1 text-sm font-semibold text-gray-500">
                  Review customer accounts separately from admins. Super admin can edit details and reset passwords.
                </p>
              </div>
            </div>
            <StatusPill tone="neutral">{normalUsers.length} users</StatusPill>
          </div>

          <div className="mt-6 space-y-3">
            {normalUsers.length ? (
              normalUsers.map(renderUserCard)
            ) : (
              <div className="rounded-2xl border border-dashed border-gray-200 px-6 py-10 text-center">
                <Users className="mx-auto text-gray-300" size={34} />
                <h3 className="mt-3 text-lg font-black text-gray-900">No normal users found</h3>
                <p className="mt-1 text-sm font-semibold text-gray-500">Customer accounts will appear here after registration or checkout.</p>
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
